import * as B from '@blueprintjs/core';
import moment from 'moment';
import React from 'react';

import type {ApiPaymentMethod} from 'app/modules/Remote/Organization';

import {AddPaymentMethodDialog} from '../AddPaymentMethodDialog';
import {
  BILLING_NOT_CONFIGURED_MESSAGE,
  usePaymentMethods,
  useRemovePaymentMethod,
  useSetDefaultPaymentMethod,
} from '../hooks';
import {PaymentMethodLabel} from '../PaymentMethodLabel';
import {paymentMethodName} from '../paymentMethodName';
import cs from './styles.module.scss';

const DEFAULT_SELECT_ID = 'payment-methods-default-select';

const REASON_TAGS = {expired: 'Expired', blocked: 'Blocked', unverified: 'Unverified'} as const;

/** Why a saved method can't be charged, or null when it can. */
function unusableReason(paymentMethod: ApiPaymentMethod): keyof typeof REASON_TAGS | null {
  if (paymentMethod.type === 'card') {
    return paymentMethod.isExpired ? 'expired' : null;
  }
  if (paymentMethod.isBlocked) {
    return 'blocked';
  }
  return paymentMethod.pendingVerification ? 'unverified' : null;
}

/** Expiry moves off the card's own line and onto the subline, where the expired state replaces it. */
function subline(paymentMethod: ApiPaymentMethod): React.ReactNode {
  if (paymentMethod.type === 'us_bank_account') {
    if (paymentMethod.isBlocked) {
      return 'Stripe can no longer debit this account.';
    }
    const {pendingVerification} = paymentMethod;
    if (pendingVerification) {
      return (
        <>
          Stripe will send a test deposit by{' '}
          {moment.utc(pendingVerification.arrivalDate).format('MMMM D')}.{' '}
          <a href={pendingVerification.hostedVerificationUrl} target="_blank" rel="noreferrer">
            Verify deposit
          </a>
        </>
      );
    }
    return 'Bank account';
  }
  if (paymentMethod.isExpired) {
    return 'Expired';
  }
  return `Expires ${String(paymentMethod.expMonth).padStart(2, '0')}/${String(paymentMethod.expYear).slice(-2)}`;
}

function optionLabel(paymentMethod: ApiPaymentMethod): string {
  const name = `${paymentMethodName(paymentMethod)} •••• ${paymentMethod.last4}`;
  const reason = unusableReason(paymentMethod);
  return reason ? `${name} (${reason})` : name;
}

const PaymentMethodRow: React.FunctionComponent<{
  paymentMethod: ApiPaymentMethod;
  isBusy: boolean;
  onRemove: () => void;
}> = ({paymentMethod, isBusy, onRemove}) => {
  const reason = unusableReason(paymentMethod);
  return (
    <li className={cs.row}>
      <div className={cs.identity}>
        <div className={cs.identityLine}>
          <PaymentMethodLabel method={paymentMethod} />
          {reason && (
            <B.Tag minimal round intent="warning">
              {REASON_TAGS[reason]}
            </B.Tag>
          )}
        </div>
        <div className={cs.subline}>{subline(paymentMethod)}</div>
      </div>
      <B.Button minimal small text="Remove" disabled={isBusy} onClick={onRemove} />
    </li>
  );
};

/**
 * The organization's saved cards and bank accounts, with the actions the Stripe Billing Portal used
 * to own. The remove and set-default guards live on the server; their messages render inline here
 * verbatim.
 *
 * Which method gets charged is stated once at the top of the panel rather than per row, so the
 * list below is only about which methods exist.
 */
export const SavedCardsList: React.FunctionComponent<{
  organizationId: string;
}> = ({organizationId}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const paymentMethodsQuery = usePaymentMethods(organizationId);
  const remove = useRemovePaymentMethod(organizationId);
  const setDefault = useSetDefaultPaymentMethod(organizationId);

  const isBusy = remove.isPending || setDefault.isPending;
  const actionError = remove.error ?? setDefault.error;

  const paymentMethods =
    paymentMethodsQuery.data?.kind === 'paymentMethods'
      ? paymentMethodsQuery.data.paymentMethods
      : [];

  const clearErrors = () => {
    remove.reset();
    setDefault.reset();
  };

  const handleSaved = async (paymentMethodId: string | null) => {
    setIsAddDialogOpen(false);
    // The Library checkout refuses to buy without a default, so the first saved method becomes one
    // without asking for a second step. Set-default invalidates the list on its way through.
    if (paymentMethodId && !paymentMethods.some((paymentMethod) => paymentMethod.isDefault)) {
      await setDefault.mutateAsync(paymentMethodId);
    } else {
      await paymentMethodsQuery.refetch();
    }
  };

  const assignment = () => {
    const defaultPaymentMethod = paymentMethods.find((paymentMethod) => paymentMethod.isDefault);
    return (
      <div className={cs.assignment}>
        <B.FormGroup
          label="Charged for renewals and imagery orders"
          labelFor={DEFAULT_SELECT_ID}
          className={cs.field}
        >
          <B.HTMLSelect
            id={DEFAULT_SELECT_ID}
            value={defaultPaymentMethod?.id ?? ''}
            disabled={isBusy || !paymentMethods.length}
            fill
            options={[
              ...(defaultPaymentMethod
                ? []
                : [{value: '', label: 'No default payment method', disabled: true}]),
              // An expired card or a blocked or unverified bank account can't be charged, so it
              // stays visible but unselectable.
              ...paymentMethods.map((paymentMethod) => ({
                value: paymentMethod.id,
                label: optionLabel(paymentMethod),
                disabled: unusableReason(paymentMethod) !== null,
              })),
            ]}
            onChange={(event) => {
              clearErrors();
              setDefault.mutate(event.currentTarget.value);
            }}
          />
        </B.FormGroup>
      </div>
    );
  };

  const body = () => {
    if (paymentMethodsQuery.isPending) {
      return <div className="bp5-skeleton">Loading payment methods</div>;
    }

    if (paymentMethodsQuery.isError) {
      return <div className={cs.error}>Couldn't load payment methods</div>;
    }

    if (paymentMethodsQuery.data.kind === 'notConfigured') {
      return <div className={cs.note}>{BILLING_NOT_CONFIGURED_MESSAGE}</div>;
    }

    return (
      <>
        {!!paymentMethods.length && assignment()}
        {paymentMethods.length ? (
          <ul className={cs.list}>
            {paymentMethods.map((paymentMethod) => (
              <PaymentMethodRow
                key={paymentMethod.id}
                paymentMethod={paymentMethod}
                isBusy={isBusy}
                onRemove={() => {
                  clearErrors();
                  remove.mutate(paymentMethod.id);
                }}
              />
            ))}
          </ul>
        ) : (
          <div className={cs.note}>No payment methods saved.</div>
        )}

        {actionError && <div className={cs.error}>{actionError.message}</div>}

        <div className={cs.footerActions}>
          <B.Button
            outlined
            small
            icon="plus"
            text="Add payment method"
            onClick={() => {
              clearErrors();
              setIsAddDialogOpen(true);
            }}
          />
        </div>
      </>
    );
  };

  return (
    <div className={cs.savedCards}>
      <div className={cs.toggleRow}>
        <B.Button
          minimal
          fill
          alignText="left"
          icon={isOpen ? 'chevron-up' : 'chevron-down'}
          text={isOpen ? 'Hide payment methods' : 'Manage payment methods'}
          aria-expanded={isOpen}
          aria-controls="payment-methods-panel"
          onClick={() => setIsOpen((open) => !open)}
        />
      </div>

      {isOpen && (
        <div className={cs.panel} id="payment-methods-panel">
          <div className={cs.panelInner}>{body()}</div>
        </div>
      )}

      <AddPaymentMethodDialog
        organizationId={organizationId}
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
};
